/**
 * Search Button - Floating search trigger button
 * Created by neo on 21.01.26
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useTrans } from '../../Store/System/LocalizationStore';

const pulse = keyframes`
  0%, 100% { box-shadow: 0 4px 20px rgba(36, 220, 164, 0.3); }
  50% { box-shadow: 0 4px 30px rgba(36, 220, 164, 0.5); }
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 28px;
  background: white;
  border: 2px solid #f0f0f0;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 500;
  color: #888;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);

  &:hover {
    border-color: #00dda2;
    color: #1a1a2e;
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(36, 220, 164, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SearchIcon = styled.span`
  font-size: 18px;
`;

const Shortcut = styled.span`
  display: none;
  padding: 4px 8px;
  background: #f6f6f7;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #888;

  @media (min-width: 768px) {
    display: inline-block;
  }
`;

export type SearchButtonProps = {
  onClick: () => void;
};

export const SearchButton: React.FC<SearchButtonProps> = observer(({ onClick }) => {
  const { t } = useTrans();

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onClick();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClick]);

  return (
    <Button onClick={onClick}>
      <SearchIcon>🔍</SearchIcon>
      {t('searchButton.placeholder', { defaultValue: 'Search...' })}
      <Shortcut>⌘K</Shortcut>
    </Button>
  );
});
